import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import { ReactComponent as SpinnerIcon } from 'component-lib/atoms/icon/SpinnerIcon.svg';

export type ButtonProps = ButtonAnchorProps | ButtonButtonProps | ButtonLinkProps;

export type ButtonAnchorProps = Omit<React.ComponentPropsWithoutRef<'a'>, OmittedProps> &
  AdditionalProps & { href: string };
export type ButtonLinkProps = Omit<LinkProps, OmittedProps> & AdditionalProps;
export type ButtonButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, OmittedProps> & AdditionalProps;

type OmittedProps = 'prefix';

interface AdditionalProps {
  /**
   * Whether or not the button is active. Used for dropdown menu toggles when
   * the dropdown is open.
   */
  active?: boolean;

  /**
   * Whether the button should be in a loading state
   */
  loading?: boolean;

  /**
   * The button's label.
   */
  children?: React.ReactNode;

  /**
   * Whether or not the button is disabled. Works even if the button is a link.
   */
  disabled?: boolean;

  /**
   * Content (usually an icon, 16x16 ones work best) to replace the button's
   * label. Screen readers would skip and read the original label instead.
   */
  infix?: React.ReactNode;

  /**
   * Content (usually an icon, 16x16 ones work best) to place after the button's
   * label. Screen readers will skip it.
   */
  postfix?: React.ReactNode;

  /**
   * How to position the postfix relatively to the button's label.
   */
  postfixPosition?: AffixPosition;

  /**
   * Same as {@link AdditionalProps.postfix}, but before the button's label.
   */
  prefix?: React.ReactNode;

  /**
   * Same as {@link AdditionalProps.postfixPosition}, but for the prefix.
   */
  prefixPosition?: AffixPosition;

  variant?: Variant;
}

type AffixPosition = 'close' | 'away';
export type Variant = 'primary' | 'outline' | 'warn' | 'danger';

export const Button: {
  (props: ButtonAnchorProps & { ref?: React.Ref<HTMLAnchorElement> }): React.ReactNode;
  (props: ButtonButtonProps & { ref?: React.Ref<HTMLButtonElement> }): React.ReactNode;
  (props: ButtonLinkProps & { ref?: React.Ref<HTMLAnchorElement> }): React.ReactNode;
  (props: ButtonProps & { ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement> }): React.ReactNode;
} = forwardRef(
  (
    {
      active = false,
      loading = false,
      children,
      disabled = false,
      infix,
      postfix,
      postfixPosition = 'close',
      prefix,
      prefixPosition = 'close',
      variant = 'outline',
      className,
      ...polymorphicProps
    },
    ref,
  ) => {
    disabled ||= loading;

    const content = (
      <>
        {prefix && (
          <Prefix position={prefixPosition} isInvisible={loading}>
            {prefix}
          </Prefix>
        )}

        {infix && (
          <Infix label={children} isInvisible={loading}>
            {infix}
          </Infix>
        )}

        {!infix && <span className={clsx('truncate', loading && 'invisible')}>{children}</span>}

        {postfix && (
          <Postfix isInvisible={loading} position={postfixPosition}>
            {postfix}
          </Postfix>
        )}

        {loading && (
          <span className="absolute inset-0 flex justify-center items-center">
            <SpinnerIcon aria-label="loading" />
          </span>
        )}
      </>
    );

    const classes = clsx(
      buttonClasses({ isActive: active, isDisabled: disabled, isTextless: !!infix, variant }),
      className,
    );

    if ('to' in polymorphicProps) {
      return (
        <Link
          aria-disabled={disabled}
          role="button"
          className={classes}
          {...polymorphicProps}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {content}
        </Link>
      );
    }

    if ('href' in polymorphicProps) {
      return (
        <a
          aria-disabled={disabled}
          role="button"
          className={classes}
          {...polymorphicProps}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        disabled={disabled}
        type="button"
        className={classes}
        {...polymorphicProps}
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
      >
        {content}
      </button>
    );
  },
);

const Postfix = ({
  children,
  position,
  isInvisible,
}: {
  children?: React.ReactNode;
  position: AffixPosition;
  isInvisible?: boolean;
}) => (
  <AffixContainer aria-hidden className={clsx('pl-1', position === 'away' && 'ml-auto', isInvisible && 'invisible')}>
    {children}
  </AffixContainer>
);

const Infix = ({
  children,
  label,
  isInvisible,
}: { children?: React.ReactNode; label?: React.ReactNode; isInvisible?: boolean } = {}) => (
  <>
    <AffixContainer className={clsx(isInvisible && 'invisible')} aria-hidden>
      {children}
    </AffixContainer>
    {label && <span className="sr-only">{label}</span>}
  </>
);

const Prefix = ({
  children,
  position,
  isInvisible,
}: {
  children?: React.ReactNode;
  position: AffixPosition;
  isInvisible?: boolean;
}) => (
  <AffixContainer aria-hidden className={clsx('pr-1', position === 'away' && 'mr-auto', isInvisible && 'invisible')}>
    {children}
  </AffixContainer>
);

interface ButtonStyleArgs {
  isActive: boolean;
  isDisabled: boolean;
  isTextless: boolean;
  variant: Variant;
}

const buttonClasses = ({ isActive, isDisabled, isTextless, variant }: ButtonStyleArgs) =>
  clsx(
    'align-middle',
    'border',
    'outline-none',
    'inline-flex',
    'items-center',
    'justify-center',
    'min-h-[2.5rem]',
    'min-w-[2.5rem]',
    'rounded-lg',
    'text-sm/snug',
    'font-semibold',
    'whitespace-nowrap',
    'transition duration-fast',

    // Normalize the centering between <a> and <button> elements
    'text-center',

    // Prevent .sr-only elements from messing up scroll behavior
    // https://github.com/tailwindlabs/tailwindcss/issues/4330
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1154640
    'relative',

    // The button will more eagerly try to stick to its square shape defined by
    // min-width and min-height:
    isTextless ? 'px-1' : 'px-2',

    // Disabled state base:
    isDisabled && 'pointer-events-none opacity-40',

    // "Primary" variant:
    variant === 'primary' && [
      'bg-primary text-white',
      'hover:bg-primary/80',
      'active:bg-primary/60',
      'focus-visible:border-secondary focus-visible:ring-4 focus-visible:ring-secondary',
      isActive ? 'border-secondary ring-4 ring-secondary' : 'border-primary',
    ],

    // "Outline" variant:
    variant === 'outline' && [
      'bg-white text-primary-medium',
      'hover:bg-primary-medium/5',
      'active:bg-primary-medium/10',
      'focus-visible:border-secondary focus-visible:ring-4 focus-visible:ring-secondary-lighter',
      isActive ? 'border-secondary ring-4 ring-secondary-lighter' : 'border-primary-medium/20',
    ],

    // "Warn" variant:
    variant === 'warn' && [
      'bg-white text-error-dark',
      'hover:bg-error-dark/5',
      'active:bg-error-dark/10',
      'focus-visible:border-error focus-visible:ring-4  focus-visible:ring-error-dark/20',
      isActive ? 'border-error ring-4 ring-error-dark/20' : 'border-error-dark/20',
    ],

    // "Danger" variant:
    variant === 'danger' && [
      'bg-error text-white',
      'hover:bg-error/80',
      'active:bg-error/60',
      'focus-visible:border-error focus-visible:ring-4 focus-visible:ring-error/20',
      isActive ? 'border-error ring-4 ring-error/20' : 'border-error',
    ],
  );

const AffixContainer = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span className={clsx('flex shrink-0 items-center justify-center', className)} {...props} />
);
